import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import { fetchLocalizedCollection } from '../lib/cms';

export default function ServicePage() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'da').then((items) => {
      const getSlug = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || Object.values(it.slug)[0]
          : it?.slug;
      const match = items.find((s) => getSlug(s) === 'service');
      if (match) setPageData(match);
    });
  }, []);

  const title = pageData?.titel || 'Service & Reparation';
  const badge = pageData?.undertitel || 'Service & Vedligehold';
  const intro =
    pageData?.intro ||
    'Når en maskine står stille, står produktionen stille. Vores erfarne serviceteknikere yder landsdækkende service, forebyggende vedligehold og akut fejlfinding på presser, pladebearbejdningsmaskiner og centralsmøringsanlæg.';
  const seoTitle = pageData?.seo_titel || 'PMB Service & Reparation – Landsdækkende industriservice';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'PMB Service & Reparation af presser, industrimaskiner og centralsmøring. Hurtig udrykning og forebyggende vedligeholdelse.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={intro} />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Service</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-radial from-[#006368]/20 to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              {badge}
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              {intro}
            </p>
          </div>
        </div>
      </section>

      {/* Service Focus Cards */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-200 hover:shadow-md transition">
              <div className="w-12 h-12 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-screwdriver-wrench" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Forebyggende Service</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Faste serviceaftaler og periodiske eftersyn forlænger levetiden på dine maskiner,
                minimerer utilsigtede nedbrud og sikrer overholdelse af gældende sikkerhedskrav.
              </p>
              <ul className="text-xs text-stone-500 space-y-2">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Gennemgang af mekaniske sliddele
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Kontrol af hydraulik og koblinger
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Test og justering af smøresystemer
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-200 hover:shadow-md transition">
              <div className="w-12 h-12 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-truck-fast" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Akut Reparation</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Hurtig udrykning og professionel fejlfinding, når uheldet er ude. Vores servicebiler
                er veludstyrede med værktøj og almindelige sliddele.
              </p>
              <ul className="text-xs text-stone-500 space-y-2">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Hurtig telefonisk support og rådgivning
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Landsdækkende udkørsel fra Sjælland
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Adgang til vores store reservedelslager
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-200 hover:shadow-md transition">
              <div className="w-12 h-12 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-arrows-rotate" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Renovering & Retrofit</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Totalrenovering af ældre presser og maskinanlæg på vores maskinfabrik i Borup eller
                opgradering af styringer, lysgitre og hydraulik.
              </p>
              <ul className="text-xs text-stone-500 space-y-2">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Moderne styringer og sikkerhedsudstyr
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Mekanisk overhaling og lejeskift
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" /> Montering af automatisk centralsmøring
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action & Contact */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-stone-900 text-white rounded-2xl p-8 sm:p-12 lg:p-16 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl">
              <span className="text-xs font-bold text-[#0d8086] uppercase tracking-wider">
                Brug for hjælp?
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold mt-2 mb-4">
                Har du brug for service eller akut maskinsupport?
              </h2>
              <p className="text-stone-300 text-sm sm:text-base leading-relaxed">
                Kontakt vores serviceafdeling direkte på telefon eller udfyld formularen. Vi sidder
                klar til at rådgive og planlægge et servicebesøg.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm font-semibold">
                <a
                  href="tel:+4557526262"
                  className="inline-flex items-center gap-2 bg-[#006368] hover:bg-[#005155] text-white px-5 py-3 rounded-lg transition"
                >
                  <i className="fa-solid fa-phone" /> Ring på 57 52 62 62
                </a>
                <a
                  href="mailto:pmb@pmb.dk"
                  className="inline-flex items-center gap-2 bg-stone-800 hover:bg-stone-700 text-white px-5 py-3 rounded-lg transition border border-stone-700"
                >
                  <i className="fa-solid fa-envelope" /> pmb@pmb.dk
                </a>
              </div>
            </div>

            <div className="w-full lg:w-96 bg-white text-stone-900 rounded-xl p-6 shadow-xl">
              <h3 className="font-bold text-base mb-2">Book eller forespørg service</h3>
              <p className="text-xs text-stone-500 mb-4">
                Udfyld formularen, og vi kontakter dig hurtigst muligt.
              </p>
              <ContactForm submitButtonText="Send serviceforespørgsel" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
